import {useState} from 'react';
import {
    Alert,
    Button,
    Code,
    CopyButton,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import {
    MdCancel,
    MdCheck,
    MdContentCopy,
} from 'react-icons/md';

import {DefectSignature} from '../types';
import {
    buildTicketBody,
    buildTicketSummary,
} from '../utils/buildTicketDraft';

// FNP-904 — "File as bug".
//
// The machine does the grouping and writes the ticket; the HUMAN authorizes it
// and owns the Jira key. That division is the deliberate alternative to
// unattended auto-filing (FNP-903 open question 1) — and this drawer stays
// useful if full automation later lands, as the manual override.
//
// Filing is a two-step paste rather than one click because there is no Jira
// integration on this platform and a browser cannot hold an API token (owner's
// call, 2026-08-14). Everything except the network call to Jira is real: the
// ticket body is generated from the signature, and the key is persisted through
// updateDefectSignature exactly as an automated path would persist it.

const JIRA_KEY_PATTERN = /^[A-Z][A-Z0-9]+-\d+$/;

const CopyField = ({
                       label,
                       value,
                       minRows,
                   }: {
    label: string,
    value: string,
    minRows?: number,
}) => (
    <Stack gap={4}>
        <Group justify={'space-between'} align={'end'}>
            <Text size={'sm'} fw={500}>{label}</Text>
            <CopyButton value={value}>
                {({
                      copied,
                      copy,
                  }) => (
                    <Button
                        size={'compact-xs'}
                        variant={'subtle'}
                        leftSection={copied ? <MdCheck/> : <MdContentCopy/>}
                        onClick={copy}
                    >
                        {copied ? 'Copied' : 'Copy'}
                    </Button>
                )}
            </CopyButton>
        </Group>
        <Textarea
            value={value}
            readOnly
            autosize
            minRows={minRows ?? 2}
            maxRows={14}
            styles={{input: {fontFamily: 'monospace', fontSize: '0.75rem'}}}
        />
    </Stack>
);

export const FileAsBugDrawer = ({
                                    record,
                                    onFile,
                                    filing,
                                    opened,
                                    onClose,
                                    ...rest
                                }: DrawerProps & {
    record: DefectSignature,
    onFile: (jiraKey: string) => void,
    filing?: boolean,
}) => {

    const [jiraKey, setJiraKey] = useState(record.jiraKey ?? '');

    const trimmed = jiraKey.trim()
                           .toUpperCase();
    // Validated rather than accepted blindly: a typo'd key renders a dead link
    // on the row forever, and the row is the only place anyone would notice.
    const keyIsValid = JIRA_KEY_PATTERN.test(trimmed);

    const summary = buildTicketSummary(record);
    const body = buildTicketBody(record);

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease'
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title='File as bug'
            padding='xl'
            size='xl'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Stack gap='lg' my='md'>
                <Alert color='blue' title='Two steps'>
                    Copy the summary and body into a new FNP issue, then paste the key back
                    here. The key is stored on this defect so the row links to the ticket and
                    the defect stops counting as unfiled.
                </Alert>

                <CopyField label='Summary' value={summary} minRows={2}/>
                <CopyField label='Description' value={body} minRows={12}/>

                <TextInput
                    label='Jira key'
                    description='The issue you just created, e.g. FNP-921.'
                    placeholder='FNP-000'
                    value={jiraKey}
                    onChange={(event) => setJiraKey(event.currentTarget.value)}
                    error={
                        jiraKey.trim() !== '' && !keyIsValid
                            ? 'Expected a key like FNP-921.'
                            : undefined
                    }
                />

                <Text size='xs' c='dimmed'>
                    Fingerprint <Code>{record.id}</Code> — stable across wakes, so this defect
                    keeps the same identity when it recurs.
                </Text>
            </Stack>

            <Group justify='space-between' mt='xl'>
                <Button
                    variant='outline'
                    leftSection={<MdCancel/>}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    leftSection={<MdCheck/>}
                    disabled={!keyIsValid || filing}
                    loading={filing}
                    onClick={() => {
                        onFile(trimmed);
                        onClose();
                    }}
                >
                    Save key
                </Button>
            </Group>
        </Drawer>
    );
};
